import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import type { ReactElement } from 'react';
import { cn } from '../util/classes.ts';

export type TabsProps = TabsPrimitive.Root.Props;

/**
 * Groups a {@link TabBar} with its {@link TabPanel}s and owns the selected value.
 * Base UI's Tabs underneath: tablist semantics, panel wiring and arrow-key
 * navigation come with it.
 */
export function Tabs({ className, ...props }: TabsProps): ReactElement {
    return (
        <TabsPrimitive.Root
            data-slot="tabs"
            className={cn('flex flex-col gap-default', className)}
            {...props}
        />
    );
}

export type TabBarProps = TabsPrimitive.List.Props;

/**
 * The tab row: labels on a hairline baseline under a sliding active marker.
 * Scrolls when the tabs don't fit, fading out rather than cutting off.
 */
export function TabBar({ className, children, ...props }: TabBarProps): ReactElement {
    return (
        <TabsPrimitive.List
            data-slot="tab-bar"
            className={cn(
                // No items-center: stretched tabs share the row height, so every
                // hover line lands on the baseline even if one tab is taller.
                'relative flex gap-relaxed overflow-x-auto scrollbar-none scroll-fade-x',
                'border-b border-line-weak',
                className,
            )}
            {...props}
        >
            {children}
            <TabsPrimitive.Indicator
                data-slot="tab-bar-indicator"
                className={cn(
                    'pointer-events-none absolute bottom-0 left-0 h-0.5 bg-content',
                    'w-(--active-tab-width) translate-x-(--active-tab-left)',
                    'transition-[translate,width]',
                )}
            />
        </TabsPrimitive.List>
    );
}

// Both label copies must lay out identically
const tabLabelClasses = 'col-start-1 row-start-1 flex items-center justify-center gap-tight';

export type TabProps = TabsPrimitive.Tab.Props;

/**
 * One tab in a {@link TabBar}, keyed by `value`. Renders a `<button>`; pass
 * `render` to make it a link.
 */
export function Tab({ className, children, ...props }: TabProps): ReactElement {
    return (
        <TabsPrimitive.Tab
            data-slot="tab"
            className={cn(
                'relative grid shrink-0 cursor-pointer select-none content-center px-tight pt-cozy pb-2.5',
                'text-body text-content-weaker transition-colors',
                'hover:text-content',
                'data-active:text-body-emphasis data-active:text-content',
                'state-disabled:pointer-events-none state-disabled:opacity-disabled',
                'active:translate-px',
                'after:absolute after:inset-x-0 after:bottom-0 after:h-px after:transition-colors',
                'not-data-active:hover:after:bg-line',
                className,
            )}
            {...props}
        >
            {/* Invisible bold copy reserves the width, so going bold shifts nothing. */}
            <span aria-hidden className={cn(tabLabelClasses, 'invisible text-body-emphasis')}>
                {children}
            </span>
            <span className={tabLabelClasses}>{children}</span>
        </TabsPrimitive.Tab>
    );
}

export type TabPanelProps = TabsPrimitive.Panel.Props;

/** The content for one {@link Tab}, matched to it by `value`. */
export function TabPanel(props: TabPanelProps): ReactElement {
    return <TabsPrimitive.Panel data-slot="tab-panel" {...props} />;
}
